// src/pages/Schedule/DailySchedule.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { collection, doc, getDoc, getDocs, serverTimestamp, setDoc } from "firebase/firestore";
import { db, getOpenPunch } from "../../lib/firebase";
import { BLOCKS } from "./config/scheduleConfig";
import "./Schedule.css";

const SCHEDULES_COLL = "schedules";
const DAILY_OPS_COLL = "dailyOps";

const EXTRA_SLOTS_FIELD = "__extraSlots";
const ASSIGNMENTS_FIELD = "__assignments";
const BLOCKED_FIELD = "__blockedSlots";
const UNIT_TEXT_FIELD = "__unitText";

function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
}

function isISODate(s) {
  return typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s);
}

function parseISODateLocal(iso) {
  return new Date(`${iso}T00:00:00`);
}

function prettyLongDate(iso) {
  try {
    return parseISODateLocal(iso).toLocaleDateString(undefined, {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function uid(prefix = "id") {
  return `${prefix}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function timeToMinutes(t) {
  if (!t || typeof t !== "string" || !/^\d{2}:\d{2}$/.test(t)) return null;
  const [hh, mm] = t.split(":").map(Number);
  return hh * 60 + mm;
}

function sortAssignments(list) {
  return [...(list || [])].sort((a, b) => {
    const aS = timeToMinutes(a.start) ?? 0;
    const bS = timeToMinutes(b.start) ?? 0;
    if (aS !== bS) return aS - bS;
    const aE = timeToMinutes(a.end) ?? 0;
    const bE = timeToMinutes(b.end) ?? 0;
    return aE - bE;
  });
}

function getEmployeeName(emp) {
  return emp?.name || emp?.displayName || emp?.email || "Unknown";
}

function getEmployeePosition(emp) {
  return emp?.position || emp?.title || emp?.role || emp?.basePosition || emp?.providerLevel || "";
}

function getRoleDefaultTimes(roleLabel) {
  const role = String(roleLabel || "").trim();

  if (
    role === "Incident Command" ||
    role === "Unified Command" ||
    role === "Planning" ||
    role === "Logistics" ||
    role === "Ops Manager"
  ) {
    return { start: "05:00", end: "19:00" };
  }

  if (
    role === "Dispatcher" ||
    role === "Dispatcher 2" ||
    role === "Triage Supervisor" ||
    role.startsWith("Ops Supervisor")
  ) {
    return { start: "05:30", end: "19:00" };
  }

  return { start: "06:00", end: "19:00" };
}

function getDisplayRoleLabel(roleLabel) {
  const map = {
    "Incident Command": "Incident Cmd",
    "Unified Command": "Unified Cmd",
    "Triage Supervisor": "Triage Sup",
    "Ops Supervisor 1": "Ops Sup 1",
    "Ops Supervisor 2": "Ops Sup 2",
    "Ambulance (ALS)": "Ambulance",
    "Ambulance (BLS)": "Ambulance",
    Dispatcher: "Disp 1",
    "Dispatcher 2": "Disp 2",
  };

  return map[roleLabel] || roleLabel;
}

function getDerivedBaseLabel(roleKey) {
  const key = String(roleKey || "").trim();

  return key
    .replace(/__(paramedic|partner)$/i, "")
    .replace(/_/g, " ")
    .trim();
}

function normalizeRoleForBlockMapping(label = "") {
  const raw = String(label || "").trim();

  const explicitMap = {
    "Ops Sup 1": "Ops Supervisor 1",
    "Ops Sup 2": "Ops Supervisor 2",
    "Triage Sup": "Triage Supervisor",
    "Disp 1": "Dispatcher",
    "Disp 2": "Dispatcher 2",
    "ALS Amb": "Ambulance (ALS)",
  };

  if (explicitMap[raw]) return explicitMap[raw];

  if (/^RN\s+\d+$/i.test(raw)) return "RN";
  if (/^APRN\/PA\s+\d+$/i.test(raw)) return "APRN/PA";
  if (/^Paramedic\s+\d+$/i.test(raw)) return "Paramedic";
  if (/^EMT\/AEMT\s+\d+$/i.test(raw)) return "EMT/AEMT";
  if (/^Physician\s+\d+$/i.test(raw)) return "Physician";
  if (/^Tech\s+\d+$/i.test(raw)) return "Tech";
  if (/^Transport\s+\d+$/i.test(raw)) return "Transport";

  if (/^Ambulance\s*\(ALS\)(?:__|\s+)\d+$/i.test(raw)) return "Ambulance (ALS)";
  if (/^Ambulance\s*\(BLS\)(?:__|\s+)\d+$/i.test(raw)) return "Ambulance (BLS)";

  if (/^Tent\s+\d+\s+\d+$/i.test(raw)) {
    const m = raw.match(/^(Tent\s+\d+)\s+\d+$/i);
    return m ? m[1] : raw;
  }

  if (/^Cart\s+\d+\s+\d+$/i.test(raw)) {
    const m = raw.match(/^(Cart\s+\d+)\s+\d+$/i);
    return m ? m[1] : raw;
  }

  if (/^Bike\s+\d+\s+\d+$/i.test(raw)) {
    const m = raw.match(/^(Bike\s+\d+)\s+\d+$/i);
    return m ? m[1] : raw;
  }

  return raw;
}

function getDerivedBlockTitle(roleKey) {
  const rawBase = getDerivedBaseLabel(roleKey);
  const base = normalizeRoleForBlockMapping(rawBase);

  if (
    base === "Incident Command" ||
    base === "Unified Command" ||
    base === "Planning" ||
    base === "Logistics" ||
    base === "Ops Manager"
  ) {
    return "Command Team";
  }

  if (base === "Dispatcher" || base === "Dispatcher 2") {
    return "Dispatch";
  }

  if (
    base === "Triage Supervisor" ||
    base === "Physician" ||
    base === "RN" ||
    base === "APRN/PA" ||
    base === "Paramedic" ||
    base === "EMT/AEMT" ||
    base === "Transport" ||
    base === "Ambulance (ALS)" ||
    base === "Ambulance (BLS)" ||
    base === "Tech"
  ) {
    return "Medical HQ";
  }

  if (
    base === "Ops Supervisor 1" ||
    base === "Tent 3" ||
    base === "Cart 3" ||
    base === "Tent 5" ||
    base === "Cart 5" ||
    base === "Tent 9" ||
    base === "Cart 9" ||
    base === "Bike 1"
  ) {
    return "Front 9";
  }

  if (
    base === "Ops Supervisor 2" ||
    base === "Tent 12" ||
    base === "Cart 12" ||
    base === "Cart 15" ||
    base === "Tent 17" ||
    base === "Cart 17" ||
    base === "Bike 2" ||
    base === "Bike 3"
  ) {
    return "Back 9";
  }

  return "Unmapped Filled Roles";
}

function getSlotGroupingLabel(roleLabel, blockTitle) {
  const normalized = normalizeRoleForBlockMapping(roleLabel);

  if (blockTitle === "Medical HQ") {
    return normalized;
  }

  if (blockTitle === "Front 9") {
    if (
      normalized === "Supervisor" ||
      normalized === "Ops Supervisor" ||
      normalized === "Ops Supervisor 1"
    ) {
      return "Ops Supervisor 1";
    }

    if (/^Tent\s+\d+$/i.test(normalized)) return normalized;
    if (/^Cart\s+\d+$/i.test(normalized)) return normalized;
    if (/^Bike\s+\d+$/i.test(normalized)) return normalized;
  }

  if (blockTitle === "Back 9") {
    if (
      normalized === "Supervisor" ||
      normalized === "Ops Supervisor" ||
      normalized === "Ops Supervisor 2"
    ) {
      return "Ops Supervisor 2";
    }

    if (/^Tent\s+\d+$/i.test(normalized)) return normalized;
    if (/^Cart\s+\d+$/i.test(normalized)) return normalized;
    if (/^Bike\s+\d+$/i.test(normalized)) return normalized;
  }

  if (blockTitle === "Unmapped Filled Roles") {
    if (/^Tent\s+\d+$/i.test(normalized)) return normalized;
    if (/^Cart\s+\d+$/i.test(normalized)) return normalized;
    if (/^Bike\s+\d+$/i.test(normalized)) return normalized;
  }

  return normalized;
}

function normalizeDay(dayObj) {
  const day = dayObj && typeof dayObj === "object" ? { ...dayObj } : {};
  const next = { ...day };

  if (!next[ASSIGNMENTS_FIELD] || typeof next[ASSIGNMENTS_FIELD] !== "object" || Array.isArray(next[ASSIGNMENTS_FIELD])) {
    next[ASSIGNMENTS_FIELD] = {};
  }
  if (!next[BLOCKED_FIELD] || typeof next[BLOCKED_FIELD] !== "object" || Array.isArray(next[BLOCKED_FIELD])) {
    next[BLOCKED_FIELD] = {};
  }
  if (!next[EXTRA_SLOTS_FIELD] || typeof next[EXTRA_SLOTS_FIELD] !== "object" || Array.isArray(next[EXTRA_SLOTS_FIELD])) {
    next[EXTRA_SLOTS_FIELD] = {};
  }
  if (!next[UNIT_TEXT_FIELD] || typeof next[UNIT_TEXT_FIELD] !== "object" || Array.isArray(next[UNIT_TEXT_FIELD])) {
    next[UNIT_TEXT_FIELD] = {};
  }

  const allKnownRoleKeys = new Set();

  for (const block of BLOCKS) {
    for (const roleKey of block.slots || []) {
      allKnownRoleKeys.add(roleKey);
    }
  }

  for (const extras of Object.values(next[EXTRA_SLOTS_FIELD] || {})) {
    if (!Array.isArray(extras)) continue;
    for (const extra of extras) {
      if (extra?.id) allKnownRoleKeys.add(extra.id);
    }
  }

  for (const roleKey of Object.keys(next[ASSIGNMENTS_FIELD] || {})) {
    allKnownRoleKeys.add(roleKey);
  }

  for (const roleKey of Object.keys(next[UNIT_TEXT_FIELD] || {})) {
    allKnownRoleKeys.add(roleKey);
  }

  const legacyTimes = next.__times && typeof next.__times === "object" ? next.__times : {};

  for (const roleKey of Object.keys(next)) {
    if (roleKey.startsWith("__")) continue;
    allKnownRoleKeys.add(roleKey);
  }

  for (const roleKey of allKnownRoleKeys) {
    const currentAssignments = next[ASSIGNMENTS_FIELD][roleKey];

    if (Array.isArray(currentAssignments)) {
      next[ASSIGNMENTS_FIELD][roleKey] = currentAssignments
        .filter(Boolean)
        .map((a) => ({
          assignmentId: a.assignmentId || uid("asg"),
          employeeId: a.employeeId || a.id || a.empId || a,
          start: a.start || legacyTimes?.[roleKey]?.start || getRoleDefaultTimes(roleKey).start,
          end: a.end || legacyTimes?.[roleKey]?.end || getRoleDefaultTimes(roleKey).end,
        }))
        .filter((a) => a.employeeId && a.start && a.end);
      continue;
    }

    if (Array.isArray(next[roleKey])) {
      next[ASSIGNMENTS_FIELD][roleKey] = next[roleKey]
        .filter(Boolean)
        .map((employeeId) => ({
          assignmentId: uid("asg"),
          employeeId,
          start: legacyTimes?.[roleKey]?.start || getRoleDefaultTimes(roleKey).start,
          end: legacyTimes?.[roleKey]?.end || getRoleDefaultTimes(roleKey).end,
        }));
    } else if (!Array.isArray(next[ASSIGNMENTS_FIELD][roleKey])) {
      next[ASSIGNMENTS_FIELD][roleKey] = [];
    }

    next[ASSIGNMENTS_FIELD][roleKey] = sortAssignments(next[ASSIGNMENTS_FIELD][roleKey]).filter((a) => {
      const s = timeToMinutes(a.start);
      const e = timeToMinutes(a.end);
      return a.employeeId && s != null && e != null && s < e;
    });
  }

  return next;
}

function clampISOToRange(iso, startISO, endISO) {
  if (!isISODate(startISO) || !isISODate(endISO)) return iso;
  if (iso < startISO) return startISO;
  if (iso > endISO) return endISO;
  return iso;
}

function getTournamentDefaultDate(startISO, endISO) {
  const todayISO = ymd(new Date());

  if (!isISODate(startISO) || !isISODate(endISO)) return todayISO;
  if (todayISO < startISO) return startISO;
  if (todayISO > endISO) return endISO;
  return todayISO;
}

function getBlockSlotOrder(blockTitle, label) {
  const raw = String(label || "").trim();

  if (blockTitle === "Medical HQ") {
    const normalized = normalizeRoleForBlockMapping(raw);

    const order = {
      "Triage Supervisor": 1,
      "Physician": 2,
      "APRN/PA": 3,
      RN: 4,
      Paramedic: 5,
      "EMT/AEMT": 6,
      Tech: 7,
      Transport: 8,
      "Ambulance (ALS)": 9,
      "Ambulance (BLS)": 10,
      "Ambulance 1": 9,
      "Ambulance 2": 10,
    };

    return order[normalized] ?? 999;
  }

  if (blockTitle === "Front 9") {
    const normalized = normalizeRoleForBlockMapping(raw);
    const order = {
      "Ops Supervisor 1": 1,
      "Tent 3": 2,
      "Cart 3": 3,
      "Tent 5": 4,
      "Cart 5": 5,
      "Tent 9": 6,
      "Cart 9": 7,
      "Bike 1": 8,
    };
    return order[normalized] ?? 999;
  }

  if (blockTitle === "Back 9") {
    const normalized = normalizeRoleForBlockMapping(raw);
    const order = {
      "Ops Supervisor 2": 1,
      "Tent 12": 2,
      "Cart 12": 3,
      "Cart 15": 4,
      "Tent 17": 5,
      "Cart 17": 6,
      "Bike 2": 7,
      "Bike 3": 8,
    };
    return order[normalized] ?? 999;
  }

  return 999;
}

function sortSlotsForBlock(blockTitle, slots) {
  return [...slots].sort((a, b) => {
    const aOrder = getBlockSlotOrder(blockTitle, a.displayLabel || a.roleLabel);
    const bOrder = getBlockSlotOrder(blockTitle, b.displayLabel || b.roleLabel);

    if (aOrder !== bOrder) return aOrder - bOrder;

    return String(a.displayLabel || a.roleLabel).localeCompare(
      String(b.displayLabel || b.roleLabel),
      undefined,
      { numeric: true }
    );
  });
}

function isAmbulanceRoleLabel(label = "") {
  const normalized = normalizeRoleForBlockMapping(label);

  return (
    normalized === "Ambulance (ALS)" ||
    normalized === "Ambulance (BLS)" ||
    normalized.startsWith("Ambulance (ALS)") ||
    normalized.startsWith("Ambulance (BLS)")
  );
}

function makeUnitAssignmentId(roleKey, unitText = "") {
  return `unit:${roleKey}:${String(unitText || "").trim()}`;
}

export default function DailySchedule({ fieldMode = false }) {
  const [selectedDate, setSelectedDate] = useState(() => ymd(new Date()));
  const [punchFilter, setPunchFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [employeesLoading, setEmployeesLoading] = useState(true);
  const [twLoading, setTwLoading] = useState(true);
  const [errMsg, setErrMsg] = useState("");

  const [twStartISO, setTwStartISO] = useState("");
  const [twEndISO, setTwEndISO] = useState("");

  const [, setEmployees] = useState([]);
  const [employeeById, setEmployeeById] = useState(new Map());

  const [dayData, setDayData] = useState(normalizeDay({}));
  const [opsItems, setOpsItems] = useState({});
  const [savingKey, setSavingKey] = useState("");
  const [punchedInByEmployeeId, setPunchedInByEmployeeId] = useState({});

  const [personSearch, setPersonSearch] = useState("");
  const [personSearchOpen, setPersonSearchOpen] = useState(false);
  const personRowRefs = useRef({});

  const extraSlotMetaById = useMemo(() => {
    const map = new Map();

    for (const [blockTitle, extras] of Object.entries(dayData?.[EXTRA_SLOTS_FIELD] || {})) {
      if (!Array.isArray(extras)) continue;

      for (const extra of extras) {
        if (!extra?.id) continue;

        const rawLabel =
          extra.label ||
          extra.roleLabel ||
          extra.roleType ||
          extra.name ||
          getDerivedBaseLabel(extra.id);

        map.set(extra.id, {
          id: extra.id,
          blockTitle: extra.blockTitle || blockTitle || "Unmapped Filled Roles",
          roleLabel: rawLabel,
          displayRoleLabel: getDisplayRoleLabel(rawLabel),
          raw: extra,
        });
      }
    }

    return map;
  }, [dayData]);

  const loadTournamentWeek = useCallback(async () => {
    setTwLoading(true);
    try {
      const snap = await getDoc(doc(db, "appConfig", "tournamentWeek"));

      if (snap.exists()) {
        const data = snap.data() || {};
        const s = data.startDate;
        const e = data.endDate;

        if (isISODate(s) && isISODate(e)) {
          setTwStartISO(s);
          setTwEndISO(e);
          setSelectedDate((prev) => clampISOToRange(prev || getTournamentDefaultDate(s, e), s, e));
        } else if (isISODate(s)) {
          setTwStartISO(s);
          setTwEndISO(s);
          setSelectedDate(s);
        } else {
          setTwStartISO("");
          setTwEndISO("");
          setSelectedDate(ymd(new Date()));
        }
      } else {
        setTwStartISO("");
        setTwEndISO("");
        setSelectedDate(ymd(new Date()));
      }
    } catch (e) {
      console.warn("DailySchedule: tournament week config load failed:", e?.message || e);
      setTwStartISO("");
      setTwEndISO("");
      setSelectedDate(ymd(new Date()));
    } finally {
      setTwLoading(false);
    }
  }, []);

  const loadEmployees = useCallback(async () => {
    setEmployeesLoading(true);
    try {
      const snap = await getDocs(collection(db, "users"));
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const filtered = list.some((x) => "active" in x) ? list.filter((x) => x.active !== false) : list;

      filtered.sort((a, b) => String(getEmployeeName(a)).localeCompare(String(getEmployeeName(b))));

      const byId = new Map();
      for (const emp of filtered) byId.set(emp.id, emp);

      setEmployees(filtered);
      setEmployeeById(byId);
    } catch (e) {
      console.warn("DailySchedule: users collection not readable:", e?.message || e);
      setEmployees([]);
      setEmployeeById(new Map());
    } finally {
      setEmployeesLoading(false);
    }
  }, []);

  const loadDay = useCallback(async () => {
    if (twStartISO && twEndISO && selectedDate) {
      const clamped = clampISOToRange(selectedDate, twStartISO, twEndISO);
      if (clamped !== selectedDate) {
        setSelectedDate(clamped);
        return;
      }
    }

    setLoading(true);
    setErrMsg("");

    try {
      const [scheduleSnap, opsSnap] = await Promise.all([
        getDoc(doc(db, SCHEDULES_COLL, selectedDate)),
        getDoc(doc(db, DAILY_OPS_COLL, selectedDate)),
      ]);

      const nextDay = normalizeDay(scheduleSnap.exists() ? scheduleSnap.data() : {});
      const nextOps = opsSnap.exists() ? opsSnap.data()?.items || {} : {};

      setDayData(nextDay);
      setOpsItems(nextOps && typeof nextOps === "object" ? nextOps : {});
    } catch (e) {
      console.error("DailySchedule load error:", e);
      setErrMsg(e?.message || "Unable to load daily schedule.");
      setDayData(normalizeDay({}));
      setOpsItems({});
    } finally {
      setLoading(false);
    }
  }, [selectedDate, twStartISO, twEndISO]);

  useEffect(() => {
    loadTournamentWeek();
  }, [loadTournamentWeek]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  useEffect(() => {
    if (!selectedDate) return;
    loadDay();
  }, [loadDay, selectedDate]);

  const allAssignmentsRaw = useMemo(() => {
    const out = [];

    for (const roleKey of Object.keys(dayData[ASSIGNMENTS_FIELD] || {})) {
      const assignments = sortAssignments(dayData[ASSIGNMENTS_FIELD]?.[roleKey] || []);
      const extraMeta = extraSlotMetaById.get(roleKey);

      const rawRoleLabel = extraMeta?.roleLabel || getDerivedBaseLabel(roleKey);
      const normalizedRoleLabel = normalizeRoleForBlockMapping(rawRoleLabel);
      const blockTitle = extraMeta?.blockTitle || getDerivedBlockTitle(roleKey);
      const displayRoleLabel = getDisplayRoleLabel(normalizedRoleLabel);
      const groupingLabel = getSlotGroupingLabel(normalizedRoleLabel, blockTitle);

      for (const assignment of assignments) {
        out.push({
          roleKey,
          rawRoleLabel,
          roleLabel: normalizedRoleLabel,
          displayRoleLabel,
          groupingLabel,
          blockTitle,
          assignment,
          isExtraSlot: !!extraMeta,
        });
      }
    }

    return out;
  }, [dayData, extraSlotMetaById]);

  const loadPunchStatuses = useCallback(async () => {
    const ids = [...new Set(allAssignmentsRaw.map((x) => x.assignment.employeeId).filter(Boolean))];

    if (!ids.length) {
      setPunchedInByEmployeeId({});
      return;
    }

    await Promise.all(
      ids.map(async (employeeId) => {
        try {
          const open = await getOpenPunch(employeeId);
          setPunchedInByEmployeeId((prev) => ({
            ...prev,
            [employeeId]: !!open?.open,
          }));
        } catch {
          setPunchedInByEmployeeId((prev) => ({
            ...prev,
            [employeeId]: false,
          }));
        }
      })
    );
  }, [allAssignmentsRaw]);

  useEffect(() => {
    loadPunchStatuses();

    const timer = window.setInterval(() => {
      loadPunchStatuses();
    }, 5000);

    return () => window.clearInterval(timer);
  }, [loadPunchStatuses]);

 const groupedSlotsForRender = useMemo(() => {
  const blockMap = new Map();

  for (const row of allAssignmentsRaw) {
    const punchedIn = punchedInByEmployeeId[row.assignment.employeeId] === true;

    const matchesPunch =
      punchFilter === "all" ||
      (punchFilter === "in" && punchedIn) ||
      (punchFilter === "out" && !punchedIn);

    if (!matchesPunch) continue;

    const blockTitle = row.blockTitle || "Unmapped Filled Roles";
    const slotLabel = row.groupingLabel || row.displayRoleLabel || row.roleLabel || row.roleKey;
    const slotKey = `${blockTitle}:::${slotLabel}`;

    if (!blockMap.has(blockTitle)) blockMap.set(blockTitle, new Map());
    const slotMap = blockMap.get(blockTitle);

    if (!slotMap.has(slotKey)) {
      slotMap.set(slotKey, {
        roleKey: slotKey,
        slotSourceRoleKey: row.roleKey,
        roleLabel: slotLabel,
        displayLabel: getDisplayRoleLabel(slotLabel),
        assignments: [],
        unitRows: [],
        unitText: "",
        isAmbulance: isAmbulanceRoleLabel(slotLabel),
      });
    }

    slotMap.get(slotKey).assignments.push(row.assignment);
  }

  for (const [roleKey, unitText] of Object.entries(dayData?.[UNIT_TEXT_FIELD] || {})) {
    const rawRoleLabel = getDerivedBaseLabel(roleKey);
    const normalizedRoleLabel = normalizeRoleForBlockMapping(rawRoleLabel);

    if (!isAmbulanceRoleLabel(normalizedRoleLabel)) continue;
    if (!String(unitText || "").trim()) continue;

    const blockTitle = getDerivedBlockTitle(roleKey) || "Medical HQ";
    const baseLabel = normalizedRoleLabel.replace(/__\d+$/, "");
    const slotLabel = getSlotGroupingLabel(baseLabel, blockTitle);
    const slotKey = `${blockTitle}:::${slotLabel}`;

    if (!blockMap.has(blockTitle)) blockMap.set(blockTitle, new Map());
    const slotMap = blockMap.get(blockTitle);

    if (!slotMap.has(slotKey)) {
      slotMap.set(slotKey, {
        roleKey: slotKey,
        slotSourceRoleKey: roleKey,
        roleLabel: slotLabel,
        displayLabel: getDisplayRoleLabel(slotLabel),
        assignments: [],
        unitRows: [],
        unitText: "",
        isAmbulance: true,
      });
    }

    slotMap.get(slotKey).unitRows.push({
      assignmentId: makeUnitAssignmentId(roleKey, unitText),
      roleKey,
      unitText: String(unitText).trim(),
      start: getRoleDefaultTimes(normalizedRoleLabel).start,
      end: getRoleDefaultTimes(normalizedRoleLabel).end,
      isUnit: true,
    });
  }

  for (const [, slotMap] of blockMap.entries()) {
    for (const [, slot] of slotMap.entries()) {
      slot.assignments = sortAssignments(slot.assignments);
      slot.unitRows = slot.unitRows || [];
    }
  }

  const ordered = [];

  for (const block of BLOCKS) {
    if (blockMap.has(block.title)) {
      const slots = sortSlotsForBlock(
        block.title,
        Array.from(blockMap.get(block.title).values())
      );

      ordered.push({
        title: block.title,
        slots,
      });

      blockMap.delete(block.title);
    }
  }

  for (const [title, slotMap] of blockMap.entries()) {
    const slots = sortSlotsForBlock(title, Array.from(slotMap.values()));

    ordered.push({
      title,
      slots,
    });
  }

  return ordered.filter((block) => block.slots.length > 0);
}, [allAssignmentsRaw, punchedInByEmployeeId, punchFilter, dayData]);

  const allAssignmentsVisible = useMemo(() => {
    const out = [];

    for (const block of groupedSlotsForRender) {
      for (const slot of block.slots) {
        for (const assignment of slot.assignments) {
          out.push({
            blockTitle: block.title,
            roleKey: slot.roleKey,
            roleLabel: slot.roleLabel,
            displayRoleLabel: slot.displayLabel,
            assignment,
          });
        }
      }
    }

    return out;
  }, [groupedSlotsForRender]);

  const personSearchResults = useMemo(() => {
    const q = String(personSearch || "").trim().toLowerCase();
    if (!q) return [];

    const seen = new Set();
    const matches = [];

    for (const item of allAssignmentsVisible) {
      const emp = employeeById.get(item.assignment.employeeId);
      const fullName = String(getEmployeeName(emp) || "").trim();
      if (!fullName) continue;

      const parts = fullName.toLowerCase().split(/\s+/);
      const full = fullName.toLowerCase();

      const isMatch = full.includes(q) || parts.some((part) => part.startsWith(q));
      if (!isMatch) continue;

      const key = `${item.assignment.employeeId}:${item.assignment.assignmentId}`;
      if (seen.has(key)) continue;
      seen.add(key);

      matches.push({
        key,
        employeeId: item.assignment.employeeId,
        assignmentId: item.assignment.assignmentId,
        name: fullName,
        roleLabel: item.displayRoleLabel || item.roleLabel || "",
        blockTitle: item.blockTitle || "",
      });
    }

    matches.sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
    );

    return matches.slice(0, 8);
  }, [personSearch, allAssignmentsVisible, employeeById]);

  const summary = useMemo(() => {
    const totalAssigned = allAssignmentsVisible.length;
    const punchedInCount = allAssignmentsVisible.filter(
      (x) => punchedInByEmployeeId[x.assignment.employeeId] === true
    ).length;

    return {
      totalAssigned,
      punchedInCount,
      notPunchedInCount: Math.max(0, totalAssigned - punchedInCount),
    };
  }, [allAssignmentsVisible, punchedInByEmployeeId]);

 async function saveOpsItem(assignmentInfo, field, nextValue) {
  const assignmentId = assignmentInfo?.assignment?.assignmentId;
  if (!assignmentId) return;

    const employeeId = assignmentInfo?.assignment?.employeeId || "";
    const emp = employeeId ? employeeById.get(employeeId) : null;

    const nextItems = {
      ...opsItems,
      [assignmentId]: {
        ...(opsItems?.[assignmentId] || {}),
        employeeId,
        employeeName: employeeId ? getEmployeeName(emp) : "",
        unitText: assignmentInfo?.assignment?.unitText || "",
        roleKey: assignmentInfo.roleKey,
        roleLabel: assignmentInfo.roleLabel,
        start: assignmentInfo.assignment.start,
        end: assignmentInfo.assignment.end,
        isUnit: !!assignmentInfo?.assignment?.isUnit,
        [field]: nextValue,
      },
    };

    setOpsItems(nextItems);
    setSavingKey(`${assignmentId}:${field}`);

    try {
      await setDoc(
        doc(db, DAILY_OPS_COLL, selectedDate),
        {
          date: selectedDate,
          items: nextItems,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    } catch (e) {
  console.error("DailySchedule save error:", e);
  setErrMsg(e?.message || "Could not save checklist item.");
} finally {
  setSavingKey("");
}
  }

  function getOpsValue(assignmentId, field) {
    return !!opsItems?.[assignmentId]?.[field];
  }

  function jumpToPerson(result) {
    if (!result) return;

    const el = personRowRefs.current?.[result.assignmentId];

    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

      el.classList.add("daily-ops-search-hit");
      window.setTimeout(() => {
        el.classList.remove("daily-ops-search-hit");
      }, 1800);
    }

    setPersonSearch(result.name);
    setPersonSearchOpen(false);
  }

  return (
    <div className="admin-schedule daily-ops-page">
      <div className="top-bar daily-ops-topbar">
        <div className="daily-ops-topbar-inner">
          <div className="daily-ops-header-row">
            <div className="daily-ops-title-wrap">
              <h1 className="daily-ops-page-title">Daily Operations</h1>
            </div>

            <Link to="/admin" className="daily-ops-back-link">
              ← Return to Admin Portal
            </Link>
          </div>

          <div className="daily-ops-controls-grid is-simple">
            <div className="daily-ops-person-search-wrap">
              <input
                type="text"
                value={personSearch}
                placeholder="Search by first or last name..."
                className="daily-ops-person-search-input"
                onChange={(e) => {
                  setPersonSearch(e.target.value);
                  setPersonSearchOpen(true);
                }}
                onFocus={() => setPersonSearchOpen(true)}
                onBlur={() => {
                  window.setTimeout(() => setPersonSearchOpen(false), 150);
                }}
              />

              {personSearchOpen && personSearchResults.length > 0 && (
                <div className="daily-ops-person-search-results">
                  {personSearchResults.map((result) => (
                    <button
                      key={result.key}
                      type="button"
                      className="daily-ops-person-search-result"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => jumpToPerson(result)}
                    >
                      <div className="daily-ops-person-search-name">{result.name}</div>
                      <div className="daily-ops-person-search-meta">
                        {result.blockTitle} • {result.roleLabel}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="daily-ops-input-group is-simple">
              <input
                type="date"
                value={selectedDate}
                min={twStartISO || undefined}
                max={twEndISO || undefined}
                onChange={(e) => {
                  const next = e.target.value;
                  if (!next) return;
                  if (isISODate(twStartISO) && isISODate(twEndISO)) {
                    setSelectedDate(clampISOToRange(next, twStartISO, twEndISO));
                  } else {
                    setSelectedDate(next);
                  }
                }}
                className="daily-ops-date-input"
              />

              <select
                className="daily-ops-filter-select"
                value={punchFilter}
                onChange={(e) => setPunchFilter(e.target.value)}
              >
                <option value="all">All statuses</option>
                <option value="in">Punched In</option>
                <option value="out">Not Punched In</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="daily-ops-summary-row">
        <div className="daily-ops-summary-card">
          <div className="daily-ops-summary-label">Visible Assignments</div>
          <div className="daily-ops-summary-value">{summary.totalAssigned}</div>
        </div>

        <div className="daily-ops-summary-card is-good">
          <div className="daily-ops-summary-label">Punched In</div>
          <div className="daily-ops-summary-value pulse">{summary.punchedInCount}</div>
        </div>

        <div className="daily-ops-summary-card is-warn">
          <div className="daily-ops-summary-label">Not Punched In</div>
          <div className="daily-ops-summary-value pulse">{summary.notPunchedInCount}</div>
        </div>
      </div>

      {(loading || employeesLoading || twLoading) && <div style={{ padding: 12 }}>Loading…</div>}

      {!!errMsg && (
        <div
          style={{
            padding: 12,
            color: "#991b1b",
            background: "#fff1f2",
            borderRadius: 12,
            marginBottom: 10,
          }}
        >
          {errMsg}
        </div>
      )}

      {!loading && !employeesLoading && !twLoading && !errMsg && groupedSlotsForRender.length === 0 && (
        <div className="daily-ops-empty">No filled slots for {prettyLongDate(selectedDate)}.</div>
      )}

      {!loading && !employeesLoading && !twLoading && !errMsg && groupedSlotsForRender.length > 0 && (
        <div className="daily-ops-blocks">
          {groupedSlotsForRender.map((block) => (
            <div key={block.title} className="assignment-subblock daily-ops-block">
              <div className="subblock-header">
                <h4 className="assignment-title">{block.title}</h4>
              </div>

              <div className="daily-ops-slot-grid">
                {block.slots.map((slot) => (
                  <div key={slot.roleKey} className="slot assigned daily-ops-schedule-slot">
                    <div className="slot-role daily-ops-schedule-role">{slot.displayLabel}</div>

                    <div className="daily-ops-schedule-list">
                      {slot.assignments.map((assignment) => {
                        const emp = employeeById.get(assignment.employeeId);
                        const punchedIn = punchedInByEmployeeId[assignment.employeeId] === true;

                        const assignmentInfo = {
                          blockTitle: block.title,
                          roleKey: slot.roleKey,
                          roleLabel: slot.roleLabel,
                          assignment,
                        };

                        return (
                          <div
                            key={assignment.assignmentId}
                            ref={(el) => {
                              if (el) personRowRefs.current[assignment.assignmentId] = el;
                            }}
                            className={`daily-ops-schedule-row ${
                              punchedIn ? "is-punched-in" : "is-not-punched-in"
                            }`}
                          >
                          <div className="daily-ops-schedule-person">
  <div className="daily-ops-schedule-person-top">
    <div className="daily-ops-schedule-person-main">
      <div className="daily-ops-schedule-name-line">
        <span className="daily-ops-schedule-name">{getEmployeeName(emp)}</span>
        <span className={`daily-ops-punch-badge ${punchedIn ? "is-in" : "is-out"}`}>
          {punchedIn ? "In" : "Out"}
        </span>
      </div>

      <div className="daily-ops-schedule-sub">
        {assignment.start} - {assignment.end}
        {getEmployeePosition(emp) ? ` • ${getEmployeePosition(emp)}` : ""}
      </div>
    </div>
  </div>

  <div className="daily-ops-schedule-checks">
    <label
      className="daily-ops-check"
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <input
        type="checkbox"
        checked={getOpsValue(assignment.assignmentId, "cartKeys")}
        onChange={(e) => saveOpsItem(assignmentInfo, "cartKeys", e.target.checked)}
      />
      <span>Keys</span>
    </label>

    <label
      className="daily-ops-check"
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <input
        type="checkbox"
        checked={getOpsValue(assignment.assignmentId, "radio")}
        onChange={(e) => saveOpsItem(assignmentInfo, "radio", e.target.checked)}
      />
      <span>Radio</span>
    </label>

    <label
      className="daily-ops-check"
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <input
        type="checkbox"
        checked={getOpsValue(assignment.assignmentId, "foodVoucher")}
        onChange={(e) => saveOpsItem(assignmentInfo, "foodVoucher", e.target.checked)}
      />
      <span>Food</span>
    </label>

    <label
      className="daily-ops-check"
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <input
        type="checkbox"
        checked={getOpsValue(assignment.assignmentId, "rainGear")}
        onChange={(e) => saveOpsItem(assignmentInfo, "rainGear", e.target.checked)}
      />
      <span>Rain Gear</span>
    </label>

    {savingKey.startsWith(`${assignment.assignmentId}:`) && (
      <span className="daily-ops-saving">Saving…</span>
    )}
  </div>
</div>  
                          </div>
                        );
                      })}

                     {slot.unitRows?.map((unitRow) => {
  const assignmentInfo = {
    blockTitle: block.title,
    roleKey: unitRow.roleKey,
    roleLabel: slot.roleLabel,
    assignment: unitRow,
  };

  const present = getOpsValue(unitRow.assignmentId, "present");

  return (
    <div
      key={unitRow.assignmentId}
      className={`daily-ops-schedule-row ${
        present ? "is-punched-in" : "is-not-punched-in"
      } is-unit-row`}
    >
      <div className="daily-ops-schedule-person">
        <div className="daily-ops-unit-layout">
          <div className="daily-ops-unit-main">
            <div className="daily-ops-unit-title-row">
              <span className="daily-ops-unit-name">{unitRow.unitText}</span>
              <span className={`daily-ops-punch-badge ${present ? "is-in" : "is-out"}`}>
                {present ? "Present" : "Not Present"}
              </span>
            </div>

            <div className="daily-ops-unit-sub">
              {unitRow.start} - {unitRow.end} • Ambulance
            </div>
          </div>

          <div className="daily-ops-schedule-checks">
            <label
              className="daily-ops-check"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="checkbox"
                checked={present}
                onChange={(e) => saveOpsItem(assignmentInfo, "present", e.target.checked)}
              />
              <span>Present</span>
            </label>

            <label
              className="daily-ops-check"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="checkbox"
                checked={getOpsValue(unitRow.assignmentId, "radio")}
                onChange={(e) => saveOpsItem(assignmentInfo, "radio", e.target.checked)}
              />
              <span>Radio</span>
            </label>

            <label
              className="daily-ops-check"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="checkbox"
                checked={getOpsValue(unitRow.assignmentId, "foodVoucher")}
                onChange={(e) => saveOpsItem(assignmentInfo, "foodVoucher", e.target.checked)}
              />
              <span>Food</span>
            </label>

            {savingKey.startsWith(`${unitRow.assignmentId}:`) && (
              <span className="daily-ops-saving">Saving…</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
})}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="print-header">Travelers Championship Medical — Daily Operations</div>
      <div className="print-footer">Generated {new Date().toLocaleString()}</div>
    </div>
  );
}